import React, { useEffect, useState } from "react";
import { Landmark, ExternalLink, ShieldCheck, Radio } from "lucide-react";
import { getPlaceCivic } from "@/lib/api";
import { T } from "@/context/LanguageContext";

const STATUS_TONE = {
  open: "bg-amber-100 text-amber-800",
  closed: "bg-emerald-100 text-emerald-800",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.round(d / 60))}m ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  return `${Math.round(d / 86400)}d ago`;
}

/**
 * Government & Civic module driven by the official civic registry
 * (verified City resources) + live Open311 activity. Renders only for places
 * with a registry entry; otherwise nothing (article modules take over).
 */
export function CivicModule({ geoId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    getPlaceCivic(geoId).then((d) => { if (alive && d?.available) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [geoId]);

  if (!data) return null;

  return (
    <div
      className="rounded-2xl border-2 border-navy/15 bg-white p-5"
      data-testid="civic-module"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy text-white">
            <Landmark className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl font-black text-navy">{data.official_name}</h3>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" /> <T>Official government sources</T>
            </span>
          </div>
        </div>
        {data.website && (
          <a href={data.website} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-navy/90"
             data-testid="civic-website">
            <T>Official website</T> <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {data.links.map((l) => (
          <a key={l.key} href={l.url} target="_blank" rel="noopener noreferrer"
             className="group flex items-center justify-between gap-2 rounded-lg border border-border bg-parchment/40 px-3.5 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-navy/40 hover:bg-parchment"
             data-testid={`civic-link-${l.key}`}>
            <span><T>{l.label}</T></span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-50 transition-opacity group-hover:opacity-100" />
          </a>
        ))}
      </div>

      {data.recent_311?.length > 0 && (
        <div className="mt-5" data-testid="civic-311">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-oldglory-red">
            <Radio className="h-3.5 w-3.5" /> <T>Recent 311 activity</T>
          </div>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {data.recent_311.map((r, i) => (
              <li key={i} className="flex items-center justify-between gap-3 px-3.5 py-2 text-sm" data-testid="civic-311-item">
                <span className="min-w-0">
                  <span className="font-semibold text-navy">{r.service}</span>
                  {r.address && <span className="block truncate text-xs text-muted-foreground">{r.address}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {r.status && (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  )}
                  <span className="text-[11px] tabular-nums text-muted-foreground">{timeAgo(r.requested_at)}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-muted-foreground">
            <T>Source</T>: {data.source?.label}
          </p>
        </div>
      )}
    </div>
  );
}

export default CivicModule;
